import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from '../api/client';
import { API } from '../api/config';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, ArrowRight, CreditCard, Clock, CheckCircle } from 'lucide-react';

interface Transaction {
  id: string;
  payment_id: string;
  amount: number;
  currency: string;
  description: string;
  transaction_date: string;
  status: string;
}

const Transactions: React.FC = () => {
  useAuth();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchTransactions = async () => {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      try {
        const response = await axios.get(API.getTransactions(), { headers });
        setTransactions(response.data);
      } catch (err) {
        console.error('Failed to load transactions:', err);
        setError('Could not load transaction activity.');
      } finally {
        setLoading(false);
      }
    };
    fetchTransactions();
  }, []);

  const handleMakeClaim = (transaction: Transaction) => {
    navigate('/claims', { 
      state: { 
        transactionId: transaction.id,
        amount: transaction.amount,
        shopName: transaction.description,
        date: transaction.transaction_date.split('T')[0]
      } 
    });
  };

  if (loading) return <div>Loading transactions...</div>;

  return (
    <div>
      <h1 className="page-title">Transactions</h1>

      {error && (
        <div style={{ marginBottom: '24px', padding: '16px', borderRadius: '8px', background: '#FEF2F2', color: '#991B1B', border: '1px solid #FCA5A5', fontSize: '14px' }}>
          {error}
        </div>
      )}

      <div className="section-card" style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
          <thead>
            <tr style={{ background: '#F8FAFC', borderBottom: '1px solid var(--sidebar-border)' }}>
              <th style={{ padding: '16px 24px', fontSize: '12px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase' }}>Merchant</th>
              <th style={{ padding: '16px 24px', fontSize: '12px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase' }}>Date</th>
              <th style={{ padding: '16px 24px', fontSize: '12px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase' }}>Amount</th>
              <th style={{ padding: '16px 24px', fontSize: '12px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase' }}>Status</th>
              <th style={{ padding: '16px 24px', fontSize: '12px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', textAlign: 'right' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {transactions.length > 0 ? (
              transactions.map((tx) => (
                <tr key={tx.id} style={{ borderBottom: '1px solid var(--sidebar-border)' }}>
                  <td style={{ padding: '20px 24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '40px', height: '40px', background: '#F1F5F9', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748B' }}>
                        <ShoppingBag size={20} />
                      </div>
                      <div>
                        <div style={{ fontWeight: 600 }}>{tx.description}</div>
                        <div style={{ fontSize: '12px', color: '#64748B' }}>ID: {tx.id}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '20px 24px', color: '#64748B' }}>
                    {new Date(tx.transaction_date).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '20px 24px', fontWeight: 600 }}>
                    {tx.currency} {tx.amount.toFixed(2)}
                  </td>
                  <td style={{ padding: '20px 24px' }}>
                    <span className={`status-pill ${tx.status === 'SETTLED' ? 'status-active' : 'status-pending'}`} style={{ display: 'flex', alignItems: 'center', gap: '6px', width: 'fit-content' }}>
                      {tx.status === 'SETTLED' ? <CheckCircle size={14} /> : <Clock size={14} />}
                      {tx.status}
                    </span>
                  </td>
                  <td style={{ padding: '20px 24px', textAlign: 'right' }}>
                    <button 
                      className="btn btn-primary" 
                      style={{ padding: '8px 16px', fontSize: '13px' }}
                      onClick={() => handleMakeClaim(tx)}
                    >
                      Make Claim <ArrowRight size={14} />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} style={{ padding: '80px', textAlign: 'center', color: '#64748B' }}>
                  <CreditCard size={64} style={{ opacity: 0.1, marginBottom: '24px' }} />
                  <h2 style={{ color: '#0F172A', marginBottom: '8px' }}>No Transactions</h2>
                  <p>We couldn't find any transaction activity for your account.</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Transactions;
